import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "./TimePicker.css";
import { Button, IconButton, Tooltip } from "@mui/material";
import { CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";
import { ReactComponent as InfoIcon } from "../../../assets/images/info.svg";

/** Utility for startDate checks */
const getToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const isSameDay = (date1, date2) => {
  return (
    date1 &&
    date2 &&
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

const TimeInput = ({
  heading,
  value,
  onChange,
  height,
  rtcButton,
  handleRTC,
  error,
  errorMessage,
  disabled = false,
  type,
  startTime,
  startDate,
  endTime,
  minTime,
  rtcChecked,
}) => {
  const [open, setOpen] = useState(false);
  const [popupStyle, setPopupStyle] = useState({});
  const clockList = useRef(null);
  const inputRef = useRef(null);

  const [hour, setHour] = useState("");
  const [minute, setMinute] = useState("");
  const [rtcCheck, setRtcCheck] = useState(false);

  const minutesStep = 15;

  /** Sync from external value */
  useEffect(() => {
    if (value) {
      const [hr, min] = value.split(":");
      setHour(hr || "");
      setMinute(min || "");
    } else {
      setHour("");
      setMinute("");
    }
  }, [value]);

  useEffect(() => {
    setRtcCheck(!!rtcChecked);
  }, [rtcChecked]);

  /** Close popup if clicked outside */
  useEffect(() => {
    const closeOpenMenus = (e) => {
      if (clockList.current && open && !clockList.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOpenMenus);
    return () => document.removeEventListener("mousedown", closeOpenMenus);
  }, [open]);

  /** Popup styling */
  // useEffect(() => {
  //   if (open && inputRef.current) {
  //     const rect = inputRef.current.getBoundingClientRect();
  //     setPopupStyle({
  //       position: "fixed",
  //       top: rect.bottom + 4,
  //       left: rect.left,
  //       width: rect.width,
  //       zIndex: 9999,
  //     });
  //   }
  // }, [open]);

  /** StartTime restriction (now + 3h, rounded to next 15min) */
  let minStartHour = null;
  let minStartMinute = null;
  if (type === "startTime") {
    const now = new Date();
    now.setHours(now.getHours() + 3);
    minStartHour = now.getHours();
    minStartMinute = now.getMinutes();
    if (minStartMinute % minutesStep !== 0) {
      minStartMinute = Math.ceil(minStartMinute / minutesStep) * minutesStep;
      if (minStartMinute === 60) {
        minStartMinute = 0;
        minStartHour += 1;
      }
    }
  }

  /** EndTime restriction (relative to startTime) */
  /** EndTime restriction (relative to minTime passed from parent) */
  let startHour = null,
    startMinute = null;
  if (type === "endTime" && minTime) {
    const [sh, sm] = minTime.split(":");
    startHour = parseInt(sh, 10);
    startMinute = parseInt(sm, 10);
  }

  // Determine if this is an expiry time field
  const isExpiryTime =
    typeof heading === "string" &&
    (heading?.toLowerCase().includes("listing expiry time") ||
      heading?.toLowerCase().includes("terms expiry time"));

  /** Hours range, capped for startTime and endTime */
  let hours;
  if (isExpiryTime) {
    hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, "0")); // 00 to 23 only
  } else if (type === "startTime") {
    hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, "0"));
  } else {
    hours = Array.from({ length: 25 }, (_, i) => i.toString().padStart(2, "0"));
  }
  /** Build Minutes */
  let minutes =
    hour === "24" && type === "endTime"
      ? ["00"]
      : Array.from({ length: 60 }, (_, i) => i)
          .filter((i) => i % minutesStep === 0)
          .map((i) => i.toString().padStart(2, "0"));

  /** Apply startTime filtering with startDate check */
  let minHour = null;
  let minMinute = null;

  if (minTime) {
    const [mh, mm] = minTime.split(":").map(Number);
    minHour = mh;
    minMinute = mm;
  }
  if (type === "startTime" && minHour !== null && minMinute !== null) {
    hours = hours.filter((hr) => parseInt(hr, 10) >= minHour);

    if (hour === String(minHour).padStart(2, "0")) {
      minutes = minutes.filter((m) => parseInt(m, 10) >= minMinute);

      if (minutes.length === 0) {
        hours = hours.filter((hr) => parseInt(hr, 10) > minHour);
      }
    }
  }
  /** Apply endTime filtering */
  if (type === "endTime" && startHour !== null) {
    hours = hours.filter((hr) => parseInt(hr, 10) >= startHour);
    if (hour === startHour.toString().padStart(2, "0")) {
      // strictly greater minutes
      minutes = minutes.filter((m) => parseInt(m, 10) > startMinute);
      // if no minutes left in this hour, move to next hour
      if (minutes.length === 0) {
        hours = hours.filter((hr) => parseInt(hr, 10) > startHour);
      }
    }
  }

  /** Handle selection */
  const handleHourInput = (hr) => {
    setHour(hr);
    if (minute) {
      onChange(`${hr}:${minute}`);
      setOpen(false);
    }
  };

  const handleMinuteInput = (min) => {
    setMinute(min);
    if (hour) {
      onChange(`${hour}:${min}`);
      setOpen(false);
    }
  };

  /** Handle RTC (24:00) */
  const handleRTCCheck = () => {
    if (rtcCheck) {
      setRtcCheck(false);
      handleRTC("");
    } else {
      setRtcCheck(true);
      handleRTC("24:00");
    }
  };

  return (
    <div ref={inputRef} className="time-picker-wrapper">
      <div className={`display_flex_row_center contentTimeHeader`}>
        <div className="contentHeading">
          {heading}{" "}
          {!isExpiryTime && type === "startTime" && (
            <div style={{ position: "relative" }}>
              <Tooltip
                style={{ position: "absolute", top: "-22px", left: "70px" }}
                title="The start time must be at least three hours later than the current time"
              >
                <IconButton>
                  <InfoIcon style={{ width: "14px", height: "16px" }} />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </div>
        {rtcButton && (
          <div
            className={`display_flex_row_gap_4 anotherClass`}
            onClick={handleRTCCheck}
          >
            <span
              className="contentHeading"
              style={{
                color: "var(--secondary-color-text)",
                fontWeight: "500",
              }}
            >
              RTC
            </span>
            {rtcCheck ? (
              <CheckBox
                style={{
                  fontSize: "12px",
                  paddingRight: "40%",
                  paddingTop: "10%",
                  paddingLeft: "15%",
                  color: "var(--secondary-color-text)",
                }}
              />
            ) : (
              <CheckBoxOutlineBlank
                style={{
                  fontSize: "12px",
                  paddingRight: "40%",
                  paddingTop: "10%",
                  paddingLeft: "15%",
                }}
              />
            )}
          </div>
        )}
      </div>

      {/* Input Box */}
      <div
        className={`input_select_cont ${error ? "inputErrorContainer" : ""}`}
        style={{
          height: `${height || "45"}px`,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.8 : 1,
          backgroundColor: disabled ? "#e9e9e9" : "white",
        }}
        onClick={() => {
          if (!disabled) {
            if (!open && inputRef.current) {
              const rect = inputRef.current.getBoundingClientRect();
              setPopupStyle({
                position: "fixed",
                top: rect.bottom + 4,
                left: rect.left,
                width: rect.width,
                zIndex: 9999,
              });
            }
            setOpen((p) => !p);
          }
        }}
      >
        <div className="display_flex_row_center timeInputValue">
          <input
            type="text"
            maxLength={2}
            value={hour}
            disabled={disabled}
            placeholder="HH"
            className="time-input"
            onChange={(e) => {
              const val = e.target.value;
              if (/^\d{0,2}$/.test(val) && (val === "" || Number(val) <= 24)) {
                setHour(val);
                if (minute) onChange(`${val}:${minute}`);
              }
            }}
          />
          <div className="display_flex_row_center separator">:</div>
          <input
            type="text"
            maxLength={2}
            value={minute}
            disabled={disabled}
            placeholder="MM"
            className="time-input"
            onChange={(e) => {
              const val = e.target.value;
              const validOptions = ["00", "15", "30", "45"];
              if (
                val === "" ||
                validOptions.some((opt) => opt.startsWith(val))
              ) {
                setMinute(val);
                if (hour) onChange(`${hour}:${val}`);
              }
            }}
            onBlur={() => {
              const validOptions = ["00", "15", "30", "45"];
              if (!minute) return;
              if (validOptions.includes(minute)) return;

              const numVal = Number(minute);
              let nearest = validOptions.reduce((a, b) =>
                Math.abs(Number(a) - numVal) < Math.abs(Number(b) - numVal)
                  ? a
                  : b,
              );
              setMinute(nearest);
              if (hour) onChange(`${hour}:${nearest}`);
            }}
          />
        </div>
      </div>

      {/* Error */}
      {error && errorMessage && (
        <div className="timeErrorMessage">{errorMessage}</div>
      )}

      {/* Portal Popup */}
      {open &&
        ReactDOM.createPortal(
          <div className="pop-up-body" style={popupStyle} ref={clockList}>
            <div style={{ display: "flex" }}>
              <div className="hr-list">
                {hours.map((hr) => (
                  <div
                    key={hr}
                    onClick={() => handleHourInput(hr)}
                    className={`list-item ${hr === hour ? "selected" : ""}`}
                  >
                    {hr}
                  </div>
                ))}
              </div>
              <div className="hr-list">
                {minutes.map((min) => (
                  <div
                    key={min}
                    onClick={() => handleMinuteInput(min)}
                    className={`list-item ${min === minute ? "selected" : ""}`}
                  >
                    {min}
                  </div>
                ))}
              </div>
            </div>
            <div className="clock-footer">
              <Button variant="text" onClick={() => setOpen(false)}>
                OK
              </Button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default TimeInput;
